import {
    Badge,
    classNames,
    CodeDigits,
    IconNames,
    KeyPad,
    layout,
    MaterialIcon,
    PushButton,
    TextInput,
    theme,
    typography,
    useAppAdmissionListener,
    useAsyncCallback,
    useBarcodeListener,
    useCardListener,
    useIdle,
    useKeypadListener, useOccupancies,
    usePlatform,
    useStorageItemBlob,
    useTypeRef,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {AccessCode, AdmissionCode, Barcode, LogLevel, NfcCard} from "@variocube/app-api";
import {Rental, RentalTypes} from "./types";
import {useLocalization} from "../i18n";
import {useRentals, useUsers} from "./hooks";
import {em} from "csx";
import {DateTime} from "luxon";

export interface HomeViewProps {
    onCode: (code: string) => Promise<void>;
    onRentals: (rentals: Rental[]) => void;
    rentalTypes: RentalTypes;
}

export function HomeView({onCode, onRentals, rentalTypes}: HomeViewProps) {
    const platform = usePlatform();
    const {t} = useLocalization();

    const users = useUsers();
    const rentals = useRentals();
    const occupancies = useOccupancies();

    const [code, setCode] = useState("");
    const [codeVisibility, setCodeVisibility] = useState(false);
    const [barcodeError, setBarcodeError] = useState<boolean>(false);
    const [nfcKeyError, setNfcKeyError] = useState<boolean>(false);
    const [type, setType] = useTypeRef();

    const backgroundImageBlob = useStorageItemBlob("backgroundImage");
    const backgroundImageUrl = useMemo(() => backgroundImageBlob && URL.createObjectURL(backgroundImageBlob), [backgroundImageBlob]);
    const customBackground = backgroundImageUrl && style({backgroundImage: `url("${backgroundImageUrl}")`});

    const {loading, error, execute} = useAsyncCallback(async () => {
        setBarcodeError(false);
        setNfcKeyError(false);
        if(code) {
            return onCode(code);
        }
    }, [onCode, code]);

    useEffect(() => {
        if(error) {
            setCode("")
        }
    }, [error])

    const idle = useIdle(15);

    useEffect(() => {
        if (idle) {
            setCode("");
            setBarcodeError(false);
            setNfcKeyError(false);
            execute();
        }
    }, [idle, execute]);

    const handleBarcode = useCallback(async ({code}: Barcode) => {
        try {
            execute();
            setBarcodeError(false);
            await platform.log(LogLevel.Trace, "Barcode scanned", {code});
            await onCode(atob(code));
        } catch (error) {
            console.error("Error retrieving rentals for barcode");
            setBarcodeError(true);
        }
    }, [platform]);

    const handleCard = useCallback(async (nfcCard?:  NfcCard) => {
        const key = (nfcCard?.uid ?? "").trim();
        try {
            setNfcKeyError(false);
            await platform.log(LogLevel.Trace, "NFC Key detected", {key});

            const borrowerRentals = rentals.filter(r => r.borrower.nfcToken === key);
            if (borrowerRentals.length > 0) {
                onRentals(borrowerRentals);
                return;
            }

            await onCode(key);
        } catch (error) {
            console.error("Error retrieving rentals for NFC Key");
            setNfcKeyError(true);
        }
    }, [platform, rentals]);

    const handleAdmission = useCallback(async (request: AdmissionCode) => {
        console.log('variocube.rental: looks up for user with access code', request.code);
        if (users.find(u => u.accessCode === request.code)) {
            return {
                ...request,
                appId: 'variocube.rental'
            };
        }
    }, [users]);

    const handleKeypad = useCallback(async (request: AccessCode) => {
        await platform.log(LogLevel.Trace, 'Received access code from keypad', request.code);

        let code = request.code;
        let isReturn = code.startsWith('0');
        if (isReturn) {
            await platform.log(LogLevel.Audit, "Code start with 0 (ZERO). Process as return case.", code);
            code = code.substring(1);
        }

        const rental = rentals.find(r => r.borrower.accessCode === code && r.borrower.canAccess === true);
        const occupancy = occupancies.find(occupancy => occupancy.content.item === rental?.itemUuid);

        if (rental !== undefined && occupancy !== undefined) {
            await platform.openBoxLock(occupancy.boxNumber);
            if (isReturn && !!rental.borrower.pickedUp && !rental.borrower.returned) {
                let rentalEnded = DateTime.now();
                await platform.log(LogLevel.Audit, "Rental ended.", rentalEnded);
                await platform.updateOccupancy(occupancy.uuid, {
                    rentalEnded: rentalEnded.toISO()
                }, true);
            } else {
                await platform.updateOccupancy(occupancy.uuid, {
                    item: rental.itemUuid,
                    rental: rental.uuid
                });
            }
        }
    }, [platform, rentals, occupancies])

    useBarcodeListener(handleBarcode);
    useCardListener(handleCard);
    useAppAdmissionListener(handleAdmission);
    useKeypadListener(handleKeypad)

    function toggleCodeVisibility() {
        setCodeVisibility(!codeVisibility);
    }

    return (
        <View>
            <ViewHeader className={theme.lessContrast}>
                <ViewHeaderItem className={classNames(layout.paddingMedium, styles.branding)}>
                    {rentalTypes == RentalTypes.Loan ? t("appNameLoan") : t("appNameRental")}
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={classNames(layout.vertical, layout.center, layout.alignCenter, customBackground)}>
                <div className={classNames(layout.horizontal)}>
                    <div className={classNames(theme.moreContrast, styles.boxLeft)}>
                        <TextInput
                            type={codeVisibility ? 'text' : 'password'}
                            onChange={setCode}
                            value={code}
                            disabled={loading}
                            typeRef={setType}
                            endAdornment={(
                                <button onClick={toggleCodeVisibility} className={styles.visibilityButton}>
                                    <MaterialIcon icon={codeVisibility ? IconNames.visibility_off : IconNames.visibility}/>
                                </button>
                            )}
                        />
                        <KeyPad onType={type} digits={CodeDigits} disabled={loading}/>
                    </div>
                    <div
                        className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.boxRight)}>
                        <div>
                            <div className={classNames(typography.title, layout.marginYSmall)}>
                                {t("EnterAccessCode")}
                            </div>
                            <div className={typography.description}>
                                {t("EnterCodeMessage")}
                            </div>
                        </div>

                        <div>
                            {loading && (
                                <p>{t("CodeChecking")}</p>
                            )}
                            {!idle && error && (
                                <p>
                                    <Badge error>
                                        {t("AccessCodeInvalid")}
                                    </Badge>
                                </p>
                            )}
                            {!idle && barcodeError && (
                                <p>
                                    <Badge error>
                                        {t("BarCodeInvalid")}
                                    </Badge>
                                </p>
                            )}
                            {!idle && nfcKeyError && (
                                <p>
                                    <Badge error>
                                        {t("NfcKeyInvalid")}
                                    </Badge>
                                </p>
                            )}
                        </div>

                        <div>
                            <PushButton primary className={classNames(styles.button, styles.marginBottom)}
                                        onClick={execute}>
                                {t("Submit")}
                            </PushButton>
                        </div>
                    </div>
                </div>
            </ViewContent>
        </View>
    )
}

const styles = stylesheet({
    branding: {
        fontSize: "2em",
        fontWeight: 300,
        color: "var(--color-primary)"
    },
    boxLeft: {
        width: "24em"
    },
    boxRight: {
        width: "20em",
        padding: "1.5em"
    },
    button: {
        width: "100%"
    },
    marginBottom: {
        marginBottom: "0.5rem"
    },
    visibilityButton: {
        border: 0,
        backgroundColor: "transparent",
        color: "inherit",
        padding: em(0.5),
        cursor: "pointer"
    }
})
